import React from "react";
import * as T from "./types";
import * as S from "./styles";
import { useDispatch, useSelector } from "react-redux";
import C from "@assets/themes/colors";
import selectors from "@state/selectors";
import Input from "@components/Input";
import ComboBox from "@components/ComboBox";
import Chips from "@components/Chips";
import Button from "@components/Button";
import ToolTip from "@components/Tooltip";
import { UserAccessContext } from "@utils/DOIgnition/UserAccessContex";
import { AuthContext } from "@utils/DOIgnition/AuthContext";
import { IUserAccess, UserStatus } from "@state/userAccess/types";
import { capitalizeFirstLetter, emailRegex, validateNumbers } from "@utils/userUtils";
import { IDSConfig } from "@state/dsconfig/types";
import { debounce, set } from "lodash";
import { getUserAccessByEmail, postUserAccess, putUserAccess } from "@state/userAccess/utils";
import { fetchIndustries } from "@state/industries/utils";
import { addNewUserAccessInList, findBrandNames, findIndustryFromBrandId, industryToSelected, updateUserAccessInList } from "@utils/userAccess";

const uuid = require('react-uuid');

const CHECKING_EMAIL = "Checking if email is available..."
const EMAIL_ERROR_MESSAGE = "Error: Email not valid"
const EMAIL_ERROR_DUPLICATED = "Error: Email is already used"
const FIELD_ERROR_EMPTY = (field: string) => `Error: ${field} can not be an empty value`
const ITEMS_ERROR_LEAST = (field: string) => `Error: At least one ${field} should be selected`
const CLIENTID_ERROR_INVALID = 'Error: Client ids can only contain numbers'

const UserInput: React.FC<T.IUserInputProps> = (props) => {

  const [value, setValue] = props.valueState;
  const [tipLabel, setTipLabel] = props.tipLabelState ?? [undefined, undefined];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setValue(value);
    if (props.id === "email" && setTipLabel) {
      if (!value.match(emailRegex) && value !== "") {
        setTipLabel(EMAIL_ERROR_MESSAGE);
      } else {
        setTipLabel("");
      }
    }
  }

  return (
    <S.MiddleInput>
      <S.InputTitle>
        {props.title}
      </S.InputTitle>
      <Input
        id={`${props.title.toLocaleLowerCase}-input`}
        value={value}
        placeholder={props.placeholder}
        disabled={props.disabled}
        handleChange={handleChange}
        validationType={props.id === "name" ? "no-special-characters" : undefined}
        style={{width: '295px'}}
        inputStyle={{color: C.DARK_BLUE}}
        tipLabel={tipLabel}
        tipLabelColor={props.isChecking ? undefined : C.RED}
        onKeyPress={props.onKeyPress}
      />
    </S.MiddleInput>
  )
}

const UserModal: React.FC<T.IProps> = (props) => {

  const dispatch = useDispatch();
  const authContext = React.useContext(AuthContext);
  const userAccessContext = React.useContext(UserAccessContext);
  const dsConfig: IDSConfig | undefined = useSelector(selectors.dsConfig.getDSConfig);
  const industriesOptions = useSelector(selectors.industries.getIndustries);

  const [brandOptions, setBrandOptions] = React.useState<string[]>([]);
  const [userModalState, setUserModalState] = props.userModalStateProp;
  const [isSaving, setIsSaving] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(false);
  const [emailTipLabel, setEmailTipLabel] = React.useState("");
  const [nameTipLabel, setNameTipLabel] = React.useState("");
  const [clientIdTipLabel, setClientIdTipLabel] = React.useState("");
  const [brandsTipLabel, setBrandsTipLabel] = React.useState("");
  const [industriesTipLabel, setIndustriesTipLabel] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [clientId, setClientId] = React.useState<string>("");
  const [clientIds, setClientIds] = React.useState<string[]>([]);
  const [brands, setBrands] = React.useState<T.ISelectedBrand[]>();
  const [industries, setIndustries] = React.useState<T.ISelectedIndustry[]>([]);
  const [lastActiveDate, setLastActiveDate] = React.useState<Date>();

  const addUserAccess = async (userAccess: IUserAccess) => {
    setIsSaving(true);
    try {
      const userAccessResponse = await postUserAccess(dispatch, userAccess);
      if (userAccessResponse.createdDate) {
        userAccess.createdDate = userAccessResponse.createdDate
        userAccess.updateDate = userAccessResponse.updateDate
        userAccessContext.setUserAccessDetail(addNewUserAccessInList(userAccessContext.userAccessDetail, userAccess))
        closeModal();
      }
      setIsSaving(false);
    } catch(error) {
      console.error(error);
      setIsSaving(false);
    }
  }

  const editUserAccess = async (userAccess: IUserAccess) => {
    setIsSaving(true);
    try {
      const userAccessResponse = await putUserAccess(dispatch, userAccess);
      userAccess.updateDate = userAccessResponse.updateDate
      userAccessContext.setUserAccessDetail(updateUserAccessInList(userAccessContext.userAccessDetail, userAccess))
      closeModal();
      setIsSaving(false);
    } catch(error) {
      console.error(error);
      setIsSaving(false);
    }
  }

  const throwErrorIfDuplicatedEmail = async (_email: string) => {
    setIsChecking(true);
    setEmailTipLabel(CHECKING_EMAIL);
    try {
      const usersAccess = await getUserAccessByEmail(dispatch, _email);
      if (usersAccess) setEmailTipLabel(EMAIL_ERROR_DUPLICATED);
      else setEmailTipLabel("");
      setIsChecking(false);
    } catch (error) {
      console.error(error);
      setIsChecking(false);
    }
  }

  const debounceFunction = React.useCallback(
    debounce((_email: string, _emailTipLabel) => {
      if (_emailTipLabel !== EMAIL_ERROR_MESSAGE) throwErrorIfDuplicatedEmail(_email);
    }, 400), []
  );

  const removeIndustryFromChip = (index: number) => {
    setIndustries(industries.filter((i, iIndex) => iIndex !== index));
  }

  const removeIdFromChip = (index: number) => {
    setClientIds(clientIds.filter((i, iIndex) => iIndex !== index));
    console.log(clientIds)
  }

  const handleChangeAllIndustryOption = (value: string) => {
    const allIndex = industries.findIndex(i => value.indexOf(i.name) >= 0)
    if (allIndex >= 0) setIndustries(industries.map((i, iIndex) => iIndex === allIndex ? {...i, all: !i.all} : i))
  }

  const changeAllIndustriesFromChip = (index: number) => {
    const chipIndustries = industries.filter(i => i.all).map(i => `All ${i.name}`)
    handleChangeAllIndustryOption(chipIndustries[index])
  }

  const validateDuplicatedEmail = () => {
    if (email !== "" && !(props.action === "edit" && email === userModalState.userAccess?.user.email)) {
      debounceFunction(email, emailTipLabel);
    }
  }

  const handleChooseOption = (id: string, value: string) => {
    if (id === "brands-user-combobox") {
      if (brands?.find(sb => sb.name === value)) {
        setBrands(brands.filter(sb => sb.name !== value));
      } else {
        const selectedBrand = dsConfig?.brands.find(brand => brand.name === value);
        if (selectedBrand) {
          setBrands([...(brands ?? []), { id: selectedBrand.id, name: selectedBrand.name }]);
        }
      }
    } else if (id === "all-brands-user-combobox") {
      handleChangeAllIndustryOption(value)
    } else if (id === "industries-user-combobox") {
      if (industries.find(sb => sb.name === value)) {
        setIndustries(industries.filter(sb => sb.name !== value));
      } else {
        const selectedIndustry = industriesOptions.find(industry => industry.name === value);
        if (selectedIndustry) {
          setIndustries([...industries, industryToSelected(selectedIndustry)]);
        }
      }
    }
  } 

  const removeBrandFromChip = (index: number) => {
    setBrands(brands?.filter((sb, sbIndex) => sbIndex !== index));
  }

  const closeModal = () => {
    setUserModalState({});
  }

  const handleSaveUser = () => {
    let emailError = "";
    let nameError = "";
    let brandsError = "";
    let clientIdsError = "";
    var regex = /^[0-9]*$/;
    if (email === "") {
      emailError = FIELD_ERROR_EMPTY("Email");
    }
    if (name === "") {
      nameError = FIELD_ERROR_EMPTY("Name");
    }
    if (brands?.length === 0 && industries.filter(i => i.all).length === 0) {
      brandsError = ITEMS_ERROR_LEAST("brand");
    }
    if(clientIds?.length === 0){
      clientIdsError = ITEMS_ERROR_LEAST("client Ids");
    }
    if (industries.length === 0) {
      setIndustriesTipLabel(ITEMS_ERROR_LEAST("industry"));
      return;
    }
    setEmailTipLabel(emailError)
    setNameTipLabel(nameError);
    setBrandsTipLabel(brandsError);
    setClientIdTipLabel(clientIdsError);
    if (!(emailError || nameError || brandsError || clientIdsError)) {
      const currentUser = {
        email: authContext?.userData?.profile.email ?? "defaultuser@tangocode.com",
        name: authContext?.userData?.profile.name ?? "Default User",
      }
      const industriesAllAccess = industries.filter(i => i.all).map(i => i.id)
      if (props.action === "add") {
        const userAccess: IUserAccess = {
          id: uuid(),
          user: { email, name },
          status: UserStatus.ACTIVE,
          brands: brands ?? [],
          createdBy: currentUser,
          updatedBy: currentUser,
          industriesAllAccess,
          clientIds: clientIds ?? []
        };
        addUserAccess(userAccess);
      } else if (props.action === "edit" && userModalState.userAccess) {
        const userAccess: IUserAccess = {
          id: userModalState.userAccess.id,
          user: { email, name },
          status: UserStatus.ACTIVE,
          brands: brands ?? [],
          createdDate: userModalState.userAccess.createdDate,
          createdBy: userModalState.userAccess.createdBy,
          updatedBy: currentUser,
          lastActive: userModalState.userAccess.lastActive,
          industriesAllAccess,
          clientIds: clientIds ?? []
        };
        editUserAccess(userAccess);
      }
    }
  }

  const initializeParameters = (userAccess: IUserAccess) => {
    setEmail(userAccess.user.email)
    setName(userAccess.user.name)
  }

  const defineLastDate = (date?: Date) => {
    if (date) setLastActiveDate(new Date(date))
  }

  const detectIndustries = () => {
    let newIndustries: T.ISelectedIndustry[] = []
    if (userModalState.userAccess?.industriesAllAccess?.length) {
      newIndustries = userModalState.userAccess.industriesAllAccess.map(industryId => {
        const industry = industriesOptions.find(i => i.id === industryId)
        if (industry) return {...industryToSelected(industry), all: true}
      }).filter(i => i) as T.ISelectedIndustry[]
    }
    if (userModalState.userAccess?.brands?.length) {
      const brandIndustriesSet = new Set(userModalState.userAccess?.brands
        .map(brand => findIndustryFromBrandId(industriesOptions, brand.id))
        .filter(b => b))
      const brandIndustries = Array.from(brandIndustriesSet) as T.ISelectedIndustry[]
      newIndustries = [...newIndustries, ...brandIndustries.map(industryToSelected)]
    }
    setIndustries(newIndustries)
  }

  const bringIndustries = async () => {
    if (!industriesOptions.length) {
      await fetchIndustries(dispatch)
    }
  }

  const detectBrands = () => {
    const newBrandOptions = industries.filter(i => !i.all).map(i => i.brands).flat()
    const brandNames = findBrandNames(dsConfig?.brands ?? [], newBrandOptions)
    setBrandOptions(brandNames)
    if (brands) setBrands(brands.filter(b => brandNames.find(brandOption => brandOption === b.name)))
    else setBrands(userModalState.userAccess?.brands)
  }

  const handleEnterPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    var regex = /^[0-9]*$/;

    if(event.key === 'Enter' && event.currentTarget.value) {
      if(regex.test(event.currentTarget.value)){
        setClientIds([...clientIds, event.currentTarget.value]);
        setClientId(''); 
      }else{
        setClientIdTipLabel(CLIENTID_ERROR_INVALID)
      }
    }
      var key = event.key;
    
    if(!regex.test(key) &&
      key !== 'Backspace' &&
      key !== 'ArrowUp' &&
      key !== 'ArrowDown' && 
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'Tab' &&
      key !== 'v') {
   
   event.preventDefault();
 }
  }
  

  React.useEffect(() => {
    if (props.action === "edit" && userModalState.userAccess) {
      initializeParameters(userModalState.userAccess)
      defineLastDate(userModalState.userAccess?.lastActive)
      setClientIds(userModalState.userAccess?.clientIds??[])
    }
    bringIndustries()
  }, []);

  React.useEffect(() => {
    validateDuplicatedEmail();
  }, [email])

  React.useEffect(() => {
    if (name) setNameTipLabel("");
  }, [name])

  React.useEffect(() => {
    if (industriesOptions.length) detectIndustries();
  }, [industriesOptions])

  React.useEffect(() => {
    if (industries.length > 0) setIndustriesTipLabel("");
    if (industries.filter(i => i.all).length > 0) setBrandsTipLabel("");
    detectBrands()
  }, [industries])

  React.useEffect(() => {
    if (brands?.length) setBrandsTipLabel("");
  }, [brands])

  const renderEmailInput = () => {
    return (
      <UserInput
        id="email"
        title="Email*"
        placeholder="Email"
        disabled={isSaving || props.action === "edit"}
        valueState={[email, setEmail]}
        tipLabelState={[emailTipLabel, setEmailTipLabel]}
        isChecking={isChecking}
      />
    )
  }

  return (
    <S.Root>
      <S.Box>
        <S.TopContent>
          <S.Title>
            {`${capitalizeFirstLetter(userModalState.action)}${userModalState.action === "add" ? " New" : ""} User`}
          </S.Title>
          {
            props.action === "edit" &&
            <S.TopDetails>
              <S.BlueTopDetail>
                {
                  lastActiveDate ?
                    <>
                      Last Active: {lastActiveDate.toLocaleString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' })}
                      <S.GrayTopDetail>
                        { lastActiveDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) }
                      </S.GrayTopDetail>
                    </>
                    :
                    <>
                    </>
                }
              </S.BlueTopDetail>
              <S.BlueTopDetail>
                Added by: {userModalState.userAccess?.createdBy.name}
              </S.BlueTopDetail>
            </S.TopDetails>
          }
        </S.TopContent>
        <S.MiddleContent>
          <S.MiddleRow>
            {
              props.action === "edit" ?
                <ToolTip
                  tip='If you want to add a new email, please create a new user'
                >
                  { renderEmailInput() }
                </ToolTip>
                :
                renderEmailInput()
            }
            <UserInput
              id="name"
              title="Name*"
              placeholder="Enter Name"
              disabled={isSaving}
              valueState={[name, setName]}
              tipLabelState={[nameTipLabel, setNameTipLabel]}
            />
          </S.MiddleRow>
          <S.MiddleRow>
          <UserInput
                id="client-id"
                title="Client ID" 
                placeholder="Enter ID"
                disabled={isSaving}
                valueState={[clientId, setClientId]}
                onKeyPress={handleEnterPress}
                tipLabelState={[clientIdTipLabel, setClientIdTipLabel]}
              />
              <S.MiddleContent style={{width: '295px', padding: '0px 0px 0px 110px'}}>
                <Chips
                  items={clientIds.map(i => i)}
                  handleRemove={removeIdFromChip}
                />
              </S.MiddleContent>
          </S.MiddleRow>
          <S.SelectorContainer>
            <S.VerticalSelectorContainer>
              <ComboBox
                id="industries-user-combobox"
                placeholder={"Select industries"}
                multiSelectPlaceholder={"Industries"}
                title="Industries*"
                required={false}
                disabled={isSaving}
                multiSelect={true}
                options={industriesOptions.map(i => i.name)}
                position="bottom"
                values={industries.map(i => i.name)}
                chooseOption={handleChooseOption}
                containerStyle={S.ComboboxContainer}
                titleStyle={S.ComboboxTitle}
                optionContainerStyle={S.ComboboxOptionContainer}
                tipLabel={industriesTipLabel}
                tipLabelColor={C.RED}
              />
              <S.ChipsContainer>
                <Chips
                  items={industries.map(i => i.name)}
                  handleRemove={removeIndustryFromChip}
                />
              </S.ChipsContainer>
            </S.VerticalSelectorContainer>
            <S.VerticalSelectorDivider />
            <S.VerticalSelectorContainer>
              <ComboBox
                id="brands-user-combobox"
                placeholder={"Search and select brands"}
                multiSelectPlaceholder={"Brand Options"}
                title="Brand Permissions*"
                required={false}
                disabled={isSaving || industries.length === 0}
                multiSelect={true}
                allOptions={industries.map(i => `All ${i.name} Brands`)}
                options={brandOptions}
                position="bottom"
                allValues={industries.filter(i => i.all).map(i => `All ${i.name} Brands`)}
                values={brands?.map(sb => sb.name)}
                chooseOption={handleChooseOption}
                containerStyle={S.ComboboxContainer}
                titleStyle={S.ComboboxTitle}
                optionContainerStyle={S.ComboboxOptionContainer}
                tipLabel={brandsTipLabel}
                tipLabelColor={C.RED}
              />
              <S.ChipsContainer>
                <Chips
                  items={industries.filter(i => i.all).map(i => `All ${i.name}`)}
                  handleRemove={changeAllIndustriesFromChip}
                />
                <Chips
                  items={brands?.map(sb => sb.name) ?? []}
                  handleRemove={removeBrandFromChip}
                />
              </S.ChipsContainer>
            </S.VerticalSelectorContainer>
          </S.SelectorContainer>
        </S.MiddleContent>
        <S.BottomContent>
          <S.ActionsContainer>
            <Button
              onClick={closeModal}
              label="CANCEL"
              style={S.CancelButton}
              labelStyle={S.LabelCancelButton}
            />
            <Button
              onClick={handleSaveUser}
              label="SAVE"
              disabled={Boolean(emailTipLabel || nameTipLabel || industriesTipLabel || brandsTipLabel || isSaving || isChecking)}
              style={S.ConfirmButton}
            />
          </S.ActionsContainer>
        </S.BottomContent>
      </S.Box>
    </S.Root>
  )
}

export default UserModal;